// Local search client: indexes fetched catalog pages in a module worker; in-thread chunked fallback when Worker is missing.
import { useSyncExternalStore } from 'react';
import { addItems, createIndex, query, type Hit, type IndexItem, type QueryOptions } from './search-index.ts';
import type { SearchMessage } from './search.worker.ts';

const CHUNK = 2000;

let worker: Worker | null | undefined; // undefined = not tried yet
let local = createIndex();
let generation = 0;
let seq = 0;
const waiting = new Map<number, (v: unknown) => void>();
let pending = 0;
let indexed = 0;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

function getWorker(): Worker | null {
  if (worker !== undefined) return worker;
  try {
    worker = typeof Worker === 'undefined' ? null : new Worker(new URL('./search.worker.ts', import.meta.url), { type: 'module' });
  } catch {
    worker = null;
  }
  worker?.addEventListener('message', (e: MessageEvent<{ id: number; result: unknown }>) => {
    waiting.get(e.data.id)?.(e.data.result);
    waiting.delete(e.data.id);
  });
  // ponytail: a crashed worker drops to in-thread search with an empty index; pages re-feed it on their next fetch.
  worker?.addEventListener('error', () => {
    worker?.terminate();
    worker = null;
    waiting.forEach((resolve) => resolve(undefined));
    waiting.clear();
  });
  return worker;
}

function call(w: Worker, msg: SearchMessage): Promise<unknown> {
  const id = ++seq;
  return new Promise((resolve) => {
    waiting.set(id, resolve);
    w.postMessage({ ...msg, id });
  });
}

/** Adds or updates items (by id). Posted/indexed in chunks so no main-thread task grows with the catalog size. */
export async function indexCatalogItems(items: readonly IndexItem[]): Promise<void> {
  const g = generation;
  pending++;
  notify();
  try {
    for (let i = 0; i < items.length && g === generation; i += CHUNK) {
      const chunk = items.slice(i, i + CHUNK);
      const w = getWorker();
      if (w) await call(w, { type: 'add', items: chunk });
      else {
        addItems(local, chunk);
        await new Promise((r) => setTimeout(r, 0));
      }
    }
    if (g === generation) indexed += items.length;
  } finally {
    pending--;
    notify();
  }
}

export async function searchLocal(q: string, opts: QueryOptions = {}): Promise<Hit[]> {
  const w = getWorker();
  if (w) return ((await call(w, { type: 'query', q, opts })) as Hit[] | undefined) ?? query(local, q, opts);
  return query(local, q, opts);
}

/** Drops the index (scope change, sign-out). */
export function clearLocalIndex(): void {
  generation++;
  local = createIndex();
  indexed = 0;
  if (worker) void call(worker, { type: 'clear' });
  notify();
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
const isReady = () => pending === 0 && indexed > 0;
/** True once at least one batch is indexed and nothing is being indexed. */
export function useLocalIndexReady(): boolean {
  return useSyncExternalStore(subscribe, isReady, isReady);
}
